import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatChartSubtitle, formatChartTitle } from "../utils";

interface DashboardCardProps {
  title?: string;
  children: React.ReactNode;
  height?: string | number;
  subtitle?: string;
  tooltip?: string;
  details?: string;
  detailsContent?: React.ReactNode;
  titleComponent?: React.ReactNode;
}

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  children,
  subtitle,
  tooltip,
  details,
  detailsContent,
  height = "100%",
  titleComponent,
}) => {
  const theme = useTheme();
  const [isInfoOpen, setIsInfoOpen] = React.useState(false);
  const formattedTitle = formatChartTitle(title);
  const formattedSubtitle = formatChartSubtitle(subtitle);
  const tooltipText =
    tooltip ||
    formattedSubtitle ||
    (title
      ? `Use this chart to understand ${title.toLowerCase()} and compare patterns across the selected timeframe or groups.`
      : "");
  const detailsText =
    details ||
    (title
      ? `${
          formattedSubtitle ? `${formattedSubtitle} ` : ""
        }Use this view to spot changes over time, compare segments, and identify outliers worth a closer look.`
      : formattedSubtitle || "");
  const hasDetails = Boolean(detailsContent || detailsText);
  const showHeader = Boolean(title || titleComponent || tooltipText || hasDetails);

  return (
    <Card
      sx={{
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "none",
        height: height,
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: 3,
        }}
      >
        {showHeader && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              mb: title || subtitle ? 2 : 0,
              width: "100%",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, width: "100%", mb: { xs: 2, md: 0 } }}>
              {formattedTitle && (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">{formattedTitle}</Typography>
                    {tooltipText ? (
                      <Tooltip title={tooltipText} arrow>
                        <IconButton
                          size="small"
                          sx={{
                            p: 0.5,
                            color: "text.secondary",
                            "&:hover": {
                              color: "text.primary",
                            },
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                  {formattedSubtitle && (
                    <Typography variant="caption" color="text.secondary">
                      {formattedSubtitle}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end"
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              {!title && tooltipText ? (
                <Tooltip title={tooltipText} arrow>
                  <IconButton
                    size="small"
                    sx={{
                      p: 0.5,
                      color: "text.secondary",
                      "&:hover": {
                        color: "text.primary",
                      },
                    }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {hasDetails ? (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setIsInfoOpen(true)}
                >
                  More info
                </Button>
              ) : null}
              {titleComponent}
            </Stack>
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            height: "100%",
          }}
        >
          {children}
        </Box>
        {hasDetails ? (
          <Dialog
            open={isInfoOpen}
            onClose={() => setIsInfoOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>{formattedTitle || "Chart details"}</DialogTitle>
            <DialogContent>
              {detailsContent ? (
                detailsContent
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-line" }}
                >
                  {detailsText}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsInfoOpen(false)} variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        ) : null}
      </CardContent>
    </Card>
  );
};
